import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CodeWindow } from "../components/CodeWindow";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { usePageMeta, PAGE_META } from "../utils/usePageMeta";
import "./DesktopPage.css";
import "./ApiAccessPage.css";
import "../styles/api-builders.css";

const SCHEDULER_LINK = "https://calendar.app.google/CE8yHXidRV2hsSRS9";

/** Set true to show the pricing cards + calculator on /api again. */
const SHOW_API_PRICING = false;

/** The shape of every run: worker + work + resources + guardrails -> outcome. */
const ASSIGNMENT_PARTS = [
  {
    key: "worker",
    text: "The digital humanoid worker you spin up. A role, a mode, and an availability. One worker, a persistent worker, or a team.",
  },
  {
    key: "work",
    text: "The last mile in plain English: the job you need done in a portal, inbox, or system, stated like an ops brief.",
  },
  {
    key: "resources",
    text: "What the worker is allowed to use: credentials, cards, files, and tools. Set a purchase budget; execution charges are separate.",
  },
  {
    key: "guardrails",
    text: "The moments that need a human. Messages, external submissions, payments, and trades wait for approval.",
  },
  {
    key: "outcome",
    text: "Define the result you need, then inspect the returned data, cost, available evidence, and verification status.",
  },
];

const WORKER_MODES = [
  {
    value: "single",
    title: "One worker",
    text: "One assignment, one outcome, then done. The unit of work is a job, not a session.",
  },
  {
    value: "persistent",
    title: "Persistent worker",
    text: "A worker identity that keeps context and accepts new assignments as they arrive. Availability sets when work can run.",
  },
  {
    value: "team",
    title: "Worker team",
    text: "Several roles coordinating on one function, with handoffs and escalation between them.",
  },
];

const WORKER_AVAILABILITY = [
  { value: "on_demand", text: "Spin up for one job" },
  { value: "part_time", text: "Scheduled windows" },
  { value: "full_time", text: "Business hours for a function" },
  { value: "24_7", text: "Always-on operations" },
];

const WORKER_CAPABILITIES = [
  {
    title: "Autonomous digital humanoid workers",
    text: "Workers with a role, context, and tools to carry out an assignment. Managed cloud desktops are the default execution environment.",
  },
  {
    title: "Resources, not scripts",
    text: "Attach authorized credentials, files, tools, and spending limits. Workers interpret interfaces as they work and surface blockers when they cannot continue.",
  },
  {
    title: "One worker, persistent, or team",
    text: "Run a single job, keep an always-on worker, or coordinate a team that owns an entire function.",
  },
  {
    title: "Outcomes you can verify",
    text: "Inspect structured results, cost, available screenshots, and verification status. Review missing evidence and exceptions before relying on an outcome.",
  },
  {
    title: "Works solo, pairs, or teaches",
    text: "Like a person, a worker can run a job alone, pair with your team on a shared screen, or train someone step by step, and it decides which the work calls for.",
  },
  {
    title: "Work across systems, API or not",
    text: "Use supported apps and browser workflows to operate portals and authenticated services, even without APIs. Local browser access requires a separately configured connector.",
  },
];

const USE_CASE_GROUPS = [
  { id: "products", title: "Build agent products" },
  { id: "operations", title: "Run operations" },
  { id: "companies", title: "Build AI-Native Companies" },
];

const BUSINESS_MODELS = [
  {
    group: "products",
    title: "LinkedIn research agents",
    businessKind: "Specialized agent product",
    workflowKind: "function_brief",
    customerBrief:
      "Research 25 approved accounts, identify relevant people, and prepare a source-linked brief and outreach draft for each.",
    topology: "research worker + relationship context + reviewed outreach",
    text: "Build a LinkedIn agent around the work your customer needs: understand an account, research relevant people, and prepare a thoughtful first conversation. Use authorized access and keep outreach ready for review.",
    fileName: "linkedin-agent.js",
    role: "Professional Research Agent",
    availability: "on_demand",
    workerMode: "single",
    work: "Research the approved LinkedIn account list, capture source links, and draft relevant outreach without sending messages",
    vault: "vault_customer_linkedin",
    budget: 40,
    guardrails: ["messages", "external_submit"],
    outcome:
      "Return prospect briefs with role, company context, source links, missing information, and outreach drafts",
  },
  {
    group: "operations",
    title: "Business operations worker",
    businessKind: "Finance, procurement, and back office",
    workflowKind: "shift_assignment",
    customerBrief: "Reconcile invoice, vendor, and CRM records across company systems before the weekly close.",
    topology: "operations worker + finance worker + exception routing",
    text: "Delegate recurring work across departments and systems. Workers reconcile records, prepare reports, and follow through on operational queues, from a lean team to an enterprise function.",
    fileName: "internal-operations.js",
    role: "Internal Operations Team",
    availability: "24_7",
    workerMode: "team",
    work: "Reconcile invoice, vendor, and CRM records and prepare the weekly operations report",
    vault: "vault_internal_ops",
    budget: 250,
    guardrails: ["payments", "external_submit"],
    outcome: "Return reconciled records, discrepancies, source evidence, and decisions needed",
  },
  {
    group: "operations",
    title: "BPO and shared services",
    businessKind: "Client delivery and managed operations",
    workflowKind: "shift_assignment",
    customerBrief: "Process overnight portal queues across client accounts against each account's rules and service deadlines.",
    topology: "worker team per account + service rules + exception routing",
    text: "Scale service delivery with workers carrying out back-office and portal work for your clients. BPOs retain client relationships and quality standards; enterprise shared-service teams use the same infrastructure internally.",
    fileName: "bpo-shared-services.js",
    role: "Client Operations Team",
    availability: "24_7",
    workerMode: "team",
    work: "Process client portal queues against account rules and service deadlines",
    vault: "vault_client_operations",
    budget: 400,
    guardrails: ["external_submit", "payments"],
    outcome: "Return completed cases, deadline status, audit traces, and exceptions by account",
  },
  {
    group: "operations",
    title: "Customer operations",
    businessKind: "Support, onboarding, and fulfillment",
    workflowKind: "shift_assignment",
    customerBrief: "Resolve the overnight onboarding and order-status queue before the customer team starts its day.",
    topology: "support worker + fulfillment worker + policy checks",
    text: "Carry customer work through support tools, billing systems, and vendor portals. Resolve routine requests within your policies and surface the cases that need a decision.",
    fileName: "customer-operations.js",
    role: "Customer Operations Team",
    availability: "24_7",
    workerMode: "team",
    work: "Reconcile onboarding requests and order status across support, billing, and vendor systems",
    vault: "vault_customer_ops",
    budget: 200,
    guardrails: ["messages", "payments"],
    outcome: "Return resolved requests, updated records, evidence, and policy exceptions",
  },
  {
    group: "operations",
    title: "Recruitment operations",
    businessKind: "Talent acquisition and coordination",
    workflowKind: "function_brief",
    customerBrief: "Produce a qualified shortlist and coordinate interviews for three priority roles.",
    topology: "sourcing worker + research worker + scheduling worker",
    text: "Give your talent team workers for candidate research, sourcing, and interview coordination. Your people set the hiring criteria and make the hiring decisions.",
    fileName: "recruitment-operations.js",
    role: "Talent Operations Team",
    availability: "24_7",
    workerMode: "team",
    work: "Research candidates against role criteria and prepare interview coordination",
    vault: "vault_talent_ops",
    budget: 250,
    guardrails: ["messages", "external_submit"],
    outcome: "Return evidence-linked shortlists, interview schedules, and open decisions",
  },
  {
    group: "companies",
    title: "AI-native recruitment firms",
    businessKind: "AI-native company",
    workflowKind: "function_brief",
    customerBrief:
      "A client needs a qualified shortlist and interviews for three specialist roles.",
    topology: "sourcing worker + research worker + coordination worker",
    text: "Build a recruitment business where digital humanoid workers carry out sourcing, candidate research, and interview coordination. Your firm owns the client relationship and hiring judgment.",
    fileName: "recruitment-firm.js",
    role: "Recruitment Operations Team",
    availability: "24_7",
    workerMode: "team",
    work: "Source candidates against the client brief and coordinate approved interviews",
    vault: "vault_recruitment_client",
    budget: 250,
    guardrails: ["messages", "external_submit"],
    outcome:
      "Return an evidence-linked shortlist, interview schedule, and exceptions",
  },
  {
    group: "products",
    title: "Personal agents",
    businessKind: "Agent product",
    workflowKind: "function_brief",
    customerBrief:
      "A user wants tomorrow organized: research finished, calendar conflicts resolved, and follow-ups prepared.",
    topology: "persistent assistant + authorized accounts + escalation rules",
    text: "Build a personal assistant under your own brand. Your app is the interface; workers carry out research, calendar coordination, and everyday tasks across the tools each user authorizes.",
    fileName: "personal-assistant.js",
    role: "Personal Assistant",
    availability: "24_7",
    workerMode: "persistent",
    work: "Prepare tomorrow's schedule, research meeting context, and draft follow-ups",
    vault: "vault_user_workspace",
    budget: 25,
    guardrails: ["messages", "external_submit"],
    outcome:
      "Return a prepared agenda, research notes, and proposed calendar changes",
  },
  {
    group: "products",
    title: "General-purpose agents",
    businessKind: "Agent product",
    workflowKind: "function_brief",
    customerBrief:
      "A user sets a business destination and expects an agent to work across apps until the outcome is delivered.",
    topology: "persistent worker + authorized tools + outcome review",
    text: "Build an agent product like Momentum, with your own interface and workflows. GrupaAI workers carry out missions across supported apps and systems and return a trace of the work.",
    fileName: "general-purpose-agent.js",
    role: "Digital Humanoid Worker",
    availability: "24_7",
    workerMode: "persistent",
    work: "Prepare the customer launch across project, content, and support tools",
    vault: "vault_customer_apps",
    budget: 100,
    guardrails: ["messages", "external_submit", "payments"],
    outcome:
      "Return completed launch assets, evidence, and unresolved decisions",
  },
  {
    group: "products",
    title: "Vertical SaaS that does the work",
    businessKind: "Industry-specific software",
    workflowKind: "job_ticket",
    humanParallel:
      "RCM coordinator assigns one prior auth case to a portal specialist.",
    topology: "workflow worker + reviewer worker + escalation rules",
    text: "Your product triggers real portal work per case: prior auth, claims, onboarding. Structured outcomes come back.",
    fileName: "vertical-saas-workers.js",
    role: "Healthcare Ops Associate",
    availability: "on_demand",
    workerMode: "single",
    work: "Submit prior authorization for patient case #4412 in payer portal",
    vault: "vault_payer_prod",
    budget: 75,
    guardrails: ["external_submit"],
    outcome: "Return submission id, status, documents uploaded, and next steps",
  },
  {
    group: "companies",
    title: "AI-native BPO and service firms",
    businessKind: "BPO and managed operations",
    workflowKind: "shift_assignment",
    humanParallel:
      "BPO supervisor assigns overnight queue clearing for one client account.",
    topology: "persistent worker fleet + account rules + exception routing",
    text: "Build a service business around worker-delivered operations. Serve client accounts with repeatable execution, evidence, and exception handling while your team owns quality and relationships.",
    fileName: "bpo-worker-fleet.js",
    role: "BPO Operator",
    availability: "24_7",
    work: "Clear overnight vendor portal queues for Acme account and escalate exceptions",
    vault: "vault_bpo_clients",
    budget: 400,
    guardrails: ["external_submit"],
    workerMode: "persistent",
    outcome:
      "Return queue completed, exceptions, audit trace, and client-ready report",
  },
  {
    group: "companies",
    title: "Autonomous marketplaces",
    businessKind: "Supply, demand, and matching",
    workflowKind: "function_brief",
    humanParallel:
      "Marketplace ops lead assigns the team: grow supply, demand, and matches this week.",
    topology: "supply worker + demand worker + ops worker",
    text: "Run supply, demand, matching, and follow-up loops behind your marketplace, the way ops teams run liquidity today.",
    fileName: "marketplace-workers.js",
    role: "Marketplace Ops Associate",
    availability: "24_7",
    work: "Grow liquidity for our AI engineer talent marketplace this week",
    vault: "vault_marketplace_ops",
    budget: 300,
    guardrails: ["messages", "external_submit"],
    outcome:
      "Return sourced candidates, qualified buyers, matches made, and next actions",
  },
  {
    group: "companies",
    title: "AI-native VC firms",
    businessKind: "Investment research and portfolio ops",
    workflowKind: "function_brief",
    humanParallel:
      "Partner assigns analyst: produce pre-seed AI infra diligence for this month.",
    topology: "sourcing worker + diligence worker + portfolio worker",
    text: "Build an investment firm with workers carrying out sourcing, research, diligence preparation, and portfolio operations. Partners retain investment judgment and decisions.",
    fileName: "vc-firm-workers.js",
    role: "Investment Analyst",
    availability: "part_time",
    work: "Find and diligence the top AI infra startups raising pre-seed this month",
    vault: "vault_vc_research",
    budget: 500,
    outcome:
      "Return ranked companies, founder signals, diligence memo, and warm intro paths",
  },
  {
    group: "companies",
    title: "Autonomous trading research firms",
    businessKind: "Research, monitoring, and risk operations",
    workflowKind: "shift_assignment",
    humanParallel:
      "Desk lead assigns today’s monitoring brief; execution waits on approval.",
    topology: "research worker + monitoring worker + approval controls",
    text: "Build the research and operations layer of a trading firm: monitor a defined watchlist, assemble source-linked research, and prepare risk reports. Investment decisions and trade execution remain behind your approval controls.",
    fileName: "trading-workers.js",
    role: "Trading Research Team",
    workerMode: "team",
    availability: "full_time",
    work: "Monitor the approved market watchlist and prepare source-linked research and risk notes",
    vault: "vault_trading_research",
    budget: 1000,
    guardrails: ["trades"],
    outcome:
      "Return source-linked observations, risk notes, and decisions requiring review",
  },
  {
    group: "operations",
    title: "Prospect research and GTM",
    businessKind: "Account qualification and sales preparation",
    workflowKind: "shift_assignment",
    customerBrief: "Turn a target account list into qualified prospects and a source-linked brief for the sales team.",
    topology: "account researcher + qualification criteria + reviewed outreach",
    text: "Research companies and relevant people, check fit against your ideal customer profile, and collect buying signals. Give your sales team CRM-ready records and outreach drafts grounded in the sources.",
    fileName: "prospect-research.js",
    role: "GTM Research Worker",
    availability: "on_demand",
    workerMode: "single",
    work: "Qualify the approved account list against our ICP, collect source-linked buying signals, and prepare CRM records and outreach drafts",
    vault: "vault_gtm_research",
    guardrails: ["messages", "external_submit"],
    outcome: "Return qualified accounts, fit rationale, relevant contacts, source URLs, missing fields, and outreach drafts",
  },
  {
    group: "products",
    title: "Structured deep scraping",
    businessKind: "Multi-page research into usable data",
    workflowKind: "job_ticket",
    customerBrief: "Collect product and pricing records across an approved set of websites into a consistent dataset.",
    topology: "web researcher + field schema + coverage review",
    text: "Go beyond a single page. Follow relevant links across permitted sources, extract the fields your product needs, and return structured records with provenance. Define a deliverable contract in the API for JSON, CSV, or XLSX output.",
    fileName: "structured-web-data.js",
    role: "Structured Data Researcher",
    availability: "on_demand",
    workerMode: "single",
    work: "Read the approved product sites and linked detail pages; extract product name, price, currency, and source URL; report missing fields and inaccessible pages",
    guardrails: ["external_submit"],
    outcome: "Return a structured dataset, source URLs, collection timestamps, missing fields, and a coverage report",
  },
  {
    group: "products",
    title: "Food-ordering agents",
    businessKind: "Preferences to a reviewed order",
    workflowKind: "job_ticket",
    customerBrief: "Find dinner for two within a $75 purchase budget, with dietary preferences and delivery time included.",
    topology: "ordering worker + customer preferences + checkout approval",
    text: "Build ordering into your assistant or concierge product. Compare available menus, check preferences, and prepare a cart in an authorized ordering service. Ask for approval before placing the order or paying.",
    fileName: "food-ordering-agent.js",
    role: "Food Ordering Assistant",
    availability: "on_demand",
    workerMode: "single",
    work: "Compare available menus, prepare a cart for two within $75 including fees, and request approval before checkout",
    vault: "vault_food_ordering",
    card: "card_vault_xxx",
    budget: 75,
    guardrails: ["payments", "external_submit"],
    outcome: "Return the proposed order and total for review, then the receipt and delivery estimate after approved checkout",
  },
];

function WorkersRunCode({
  fileName,
  workerRole,
  mode,
  availability,
  work,
  credentials,
  card,
  budget,
  guardrailApprovals,
  outcome,
}) {
  return (
    <CodeWindow fileName={fileName} variant="editor">
      <div>
        <span className="keyword">const</span>{" "}
        <span className="variable">run</span>{" "}
        <span className="operator">=</span>{" "}
        <span className="keyword">await</span>{" "}
        <span className="variable">client</span>.
        <span className="variable">workers</span>.
        <span className="function">run</span>({`{`}
      </div>
      <div className="indent">
        <span className="property">worker</span>:{" "}
        <span className="variable">{`{`}</span>
      </div>
      {workerRole && (
        <div className="indent indent-2">
          <span className="property">role</span>:{" "}
          <span className="string">&quot;{workerRole}&quot;</span>,
        </div>
      )}
      {mode && (
        <div className="indent indent-2">
          <span className="property">mode</span>:{" "}
          <span className="string">&quot;{mode}&quot;</span>,
        </div>
      )}
      {availability && (
        <div className="indent indent-2">
          <span className="property">availability</span>:{" "}
          <span className="string">&quot;{availability}&quot;</span>,
        </div>
      )}
      <div className="indent">
        <span className="variable">{`},`}</span>
      </div>
      <div className="indent">
        <span className="property">work</span>:{" "}
        <span className="string">&quot;{work}&quot;</span>,
      </div>
      <div className="indent">
        <span className="property">resources</span>:{" "}
        <span className="variable">{`{`}</span>
      </div>
      {credentials && (
        <div className="indent indent-2">
          <span className="property">credentials</span>:{" "}
          <span className="string">&quot;{credentials}&quot;</span>,
        </div>
      )}
      {card && (
        <div className="indent indent-2">
          <span className="property">card</span>:{" "}
          <span className="string">&quot;{card}&quot;</span>,
        </div>
      )}
      {budget != null && (
        <div className="indent indent-2">
          <span className="property">budget</span>:{" "}
          <span className="number">{budget}</span>,
        </div>
      )}
      <div className="indent">
        <span className="variable">{`},`}</span>
      </div>
      {guardrailApprovals?.length > 0 && (
        <>
          <div className="indent">
            <span className="property">guardrails</span>:{" "}
            <span className="variable">{`{`}</span>
          </div>
          <div className="indent indent-2">
            <span className="property">approvals</span>:{" "}
            <span className="variable">[</span>
            {guardrailApprovals.map((approval, index) => (
              <React.Fragment key={approval}>
                <span className="string">&quot;{approval}&quot;</span>
                {index < guardrailApprovals.length - 1 && (
                  <span className="variable">, </span>
                )}
              </React.Fragment>
            ))}
            <span className="variable">]</span>,
          </div>
          <div className="indent">
            <span className="variable">{`},`}</span>
          </div>
        </>
      )}
      {outcome && (
        <div className="indent">
          <span className="property">outcome</span>:{" "}
          <span className="string">&quot;{outcome}&quot;</span>,
        </div>
      )}
      <div>{`}`});</div>
    </CodeWindow>
  );
}

const RUN_LIFECYCLE = ["running", "needs_approval", "verifying", "completed"];

const RUN_RESULT = [
  { label: "status", value: "completed", tone: "good" },
  { label: "cost.total_usd", value: "0.42" },
  { label: "verification", value: "passed, independent", tone: "good" },
  { label: "output.trace", value: "47 steps, screenshots" },
  { label: "output.recording", value: "video replay on request" },
  { label: "escalations", value: "none" },
];

const WORKFLOW_LABELS = {
  job_ticket: { text: "Job ticket", modifier: "ticket" },
  shift_assignment: { text: "Shift assignment", modifier: "shift" },
  function_brief: { text: "Function brief", modifier: "function" },
};

export function ApiAccessPage() {
  usePageMeta(PAGE_META.api);

  const [activeBusinessIndex, setActiveBusinessIndex] = useState(
    () => BUSINESS_MODELS.findIndex(model => model.fileName === "linkedin-agent.js"),
  );
  const activeBusiness = BUSINESS_MODELS[activeBusinessIndex];
  const activeWorkflow = WORKFLOW_LABELS[activeBusiness.workflowKind];
  const selectBusiness = setActiveBusinessIndex;
  const selectGroup = (group) => {
    selectBusiness(BUSINESS_MODELS.findIndex(model => model.group === group));
  };
  const navigateGroups = (event, index) => {
    const last = USE_CASE_GROUPS.length - 1;
    const next = { ArrowRight: (index + 1) % (last + 1), ArrowLeft: (index + last) % (last + 1), Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    selectGroup(USE_CASE_GROUPS[next].id);
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus();
  };

  return (
    <div className="desktop-page desktop-page--quartz api-page brand-api">
      <Navigation theme="light" />

      <main
        id="main-content"
        tabIndex="-1"
        className="desktop-main desktop-main--tight"
      >
        <section className="desktop-hero-quartz api-hero-quartz">
          <div className="desktop-hero-text-block">
            <div className="desktop-hero-badge-row">
              <span className="desktop-pill-badge desktop-pill-badge--accent">
                Cloud Workers API
              </span>
            </div>
            <h1 className="desktop-headline-quartz">
              Workers API
              <span>The cloud for autonomous workers.</span>
            </h1>
            <p className="desktop-lede-quartz api-hero-lede">
              Build a LinkedIn agent. Put a worker on business operations.
              Give your product workers that research, use software, and return
              outcomes you can verify, even without APIs.
            </p>
            <div className="desktop-download-section desktop-download-section--row">
              <a
                href={SCHEDULER_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="desktop-download-btn desktop-download-btn--primary"
              >
                Request Early Access
              </a>
              <Link
                to="/documentation"
                className="desktop-download-btn desktop-download-btn--secondary"
              >
                Read the Docs
              </Link>
            </div>
            <p
              className="api-hero-primitive"
              aria-label="worker plus work plus resources plus guardrails returns an outcome"
            >
              <code>worker</code>
              <span aria-hidden="true">+</span>
              <code>work</code>
              <span aria-hidden="true">+</span>
              <code>resources</code>
              <span aria-hidden="true">+</span>
              <code>guardrails</code>
              <span className="api-hero-primitive-arrow" aria-hidden="true">
                →
              </span>
              <code className="api-hero-primitive-out">outcome</code>
            </p>
          </div>
        </section>

        <section
          className="api-use-cases api-builder-examples"
          id="businesses"
          aria-labelledby="api-businesses-title"
        >
          <span className="api-builder-eyebrow">One API. Work at every scale.</span>
          <h2 className="desktop-section-heading" id="api-businesses-title">
            Software scales. Operations should too.
          </h2>
          <p className="desktop-section-lede">
            For enterprise teams, BPOs, growing companies, and developers. Run
            internal or outsourced operations, deliver work for clients, or build
            products and companies on the same worker infrastructure.
          </p>
          <div
            className="api-builder-foundation"
            aria-label="From your mission to the delivered outcome"
          >
            <div>
              <h3 id="api-foundation-define">You define</h3>
              <ul aria-labelledby="api-foundation-define">
                <li>The mission</li>
                <li>Systems and access</li>
                <li>Guardrails and budget</li>
              </ul>
            </div>
            <div>
              <h3 id="api-foundation-provide">GrupaAI provides</h3>
              <ul aria-labelledby="api-foundation-provide">
                <li>Digital humanoid workers</li>
                <li>Long-running execution</li>
                <li>Managed cloud desktops</li>
              </ul>
            </div>
            <div>
              <h3 id="api-foundation-receive">You receive</h3>
              <ul aria-labelledby="api-foundation-receive">
                <li>Verifiable outcomes</li>
                <li>A trace of the work</li>
                <li>Exceptions that need you</li>
              </ul>
            </div>
          </div>
          <div className="api-use-case-tabs" role="tablist" aria-label="Ways to use Workers API">
            {USE_CASE_GROUPS.map((group, index) => (
              <button
                key={group.id}
                id={`api-group-${group.id}`}
                type="button"
                role="tab"
                aria-selected={activeBusiness.group === group.id}
                aria-controls="api-use-case-panel"
                tabIndex={activeBusiness.group === group.id ? 0 : -1}
                onClick={() => selectGroup(group.id)}
                onKeyDown={(event) => navigateGroups(event, index)}
              >
                {group.title}
              </button>
            ))}
          </div>
          <div className="api-business-showcase" id="api-use-case-panel" role="tabpanel" aria-labelledby={`api-group-${activeBusiness.group}`}>
            <div className="api-business-picker">
              <label htmlFor="api-business-example-select">
                Use case
              </label>
              <select
                id="api-business-example-select"
                value={activeBusinessIndex}
                onChange={(event) => selectBusiness(Number(event.target.value))}
              >
                {USE_CASE_GROUPS.map(group => (
                  <optgroup label={group.title} key={group.id}>
                    {BUSINESS_MODELS.map((model, index) => model.group === group.id && (
                      <option value={index} key={model.title}>{model.title}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div
              className="api-business-selector"
              role="group"
              aria-label="Use case examples"
            >
              {BUSINESS_MODELS.map((model, index) => model.group === activeBusiness.group && (
                <button
                  key={model.title}
                  type="button"
                  className={`api-business-card ${index === activeBusinessIndex ? "active" : ""}`}
                  onClick={() => selectBusiness(index)}
                  aria-pressed={index === activeBusinessIndex}
                  aria-controls="api-business-preview"
                >
                  <span>{model.businessKind}</span>
                  <h3>{model.title}</h3>
                </button>
              ))}
            </div>
            <div
              className="api-business-example"
              id="api-business-preview"
              role="region"
              aria-label={activeBusiness.title + " example"}
            >
              <div className="api-business-example-copy">
                {activeWorkflow && (
                  <span
                    className={`api-workflow-badge api-workflow-badge--${activeWorkflow.modifier} api-workflow-badge--compact`}
                  >
                    {activeWorkflow.text}
                  </span>
                )}
                <h3>{activeBusiness.title}</h3>
                <p>{activeBusiness.text}</p>
                <p className="api-workflow-human">
                  <strong>Example brief:</strong>{" "}
                  {activeBusiness.customerBrief || activeBusiness.humanParallel}
                </p>
                <p className="api-workflow-output">
                  <strong>What comes back:</strong>{" "}
                  {activeBusiness.outcome.replace(/^Return /, "")}
                </p>
                <div className="api-business-shape">
                  <span className="api-business-shape-label">
                    Worker configuration
                  </span>
                  <div className="api-business-shape-chips">
                    {activeBusiness.topology.split("+").map((part) => (
                      <span key={part}>{part.trim()}</span>
                    ))}
                  </div>
                </div>
              </div>
              <WorkersRunCode
                fileName={activeBusiness.fileName}
                workerRole={activeBusiness.role}
                mode={activeBusiness.workerMode || "team"}
                availability={activeBusiness.availability}
                work={activeBusiness.work}
                credentials={activeBusiness.vault}
                card={activeBusiness.card}
                budget={activeBusiness.budget}
                guardrailApprovals={activeBusiness.guardrails}
                outcome={activeBusiness.outcome}
              />
            </div>
          </div>
          <p className="api-builder-note">
            Illustrative use cases and API assignments, not customer case
            studies or prebuilt integrations. Available through early access;
            each workflow depends on the access and tools you configure.
          </p>
        </section>

        <section className="api-assignment">
          <h2 className="desktop-section-heading">
            One call is one assignment
          </h2>
          <p className="desktop-section-lede">
            Every run has the same shape, whether it is a single ticket tonight
            or a team running a function for a month.
          </p>
          <div className="api-assignment-grid">
            <ol className="api-assignment-parts">
              {ASSIGNMENT_PARTS.map((part, index) => (
                <li key={part.key}>
                  <span className="api-assignment-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="api-assignment-part-body">
                    <code>{part.key}</code>
                    <p>{part.text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="api-assignment-code">
              <WorkersRunCode
                fileName="run-operations.js"
                workerRole="Operations Team"
                mode="team"
                availability="24_7"
                work="Clear the overnight operations queue across billing and vendor portals"
                credentials="vault_operations"
                budget={300}
                guardrailApprovals={["messages", "external_submit"]}
                outcome="Return completed cases, updated records, evidence, and exceptions"
              />
            </div>
          </div>
        </section>

        <section className="desktop-features-three">
          <h2 className="desktop-section-heading">
            Everything a worker needs to actually finish the job
          </h2>
          <p className="desktop-section-lede">
            The execution layer underneath: runtime, roles, device sessions,
            resources, guardrails, traces, and outcome delivery.
          </p>
          <div className="api-capability-grid">
            {WORKER_CAPABILITIES.map((p) => (
              <article key={p.title} className="desktop-features-three-card">
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="api-hiring">
          <h2 className="desktop-section-heading">
            Spin up a worker for the last mile
          </h2>
          <p className="desktop-section-lede">
            A worker that uses authorized apps and accounts in supported
            execution environments to carry the assignment through to an outcome.
          </p>
          <div className="api-hiring-modes">
            {WORKER_MODES.map((m) => (
              <article key={m.value} className="api-hiring-mode">
                <code>mode: &quot;{m.value}&quot;</code>
                <strong>{m.title}</strong>
                <p>{m.text}</p>
              </article>
            ))}
          </div>
          <div className="api-hiring-availability">
            <code className="api-hiring-availability-label">availability</code>
            <ul>
              {WORKER_AVAILABILITY.map((a) => (
                <li key={a.value}>
                  <code>{a.value}</code>
                  <span>{a.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {SHOW_API_PRICING && (
          <section className="pricing-section">
            {/* pricing preserved for SHOW_API_PRICING flag */}
          </section>
        )}

        <section className="api-advanced">
          <h2 className="desktop-section-heading">
            A worker that cannot prove its work is not a worker
          </h2>
          <p className="desktop-section-lede">
            Example: prepare a food order within a customer’s preferences and
            budget, request approval, and return the receipt after checkout.
          </p>
          <div className="api-outcome-grid">
            <WorkersRunCode
              fileName="commerce-worker.js"
              workerRole="Commerce Operator"
              mode="persistent"
              availability="24_7"
              work="Prepare food order #9921 and request approval before checkout"
              credentials="vault_food_ordering"
              card="card_vault_xxx"
              budget={75}
              guardrailApprovals={["payments", "external_submit"]}
              outcome="Return receipt, final cost, and delivery ETA"
            />
            <aside className="api-outcome-panel">
              <span className="api-outcome-label">Run lifecycle</span>
              <ol className="api-outcome-lifecycle">
                {RUN_LIFECYCLE.map((step) => (
                  <li key={step}>
                    <code>{step}</code>
                  </li>
                ))}
              </ol>
              <span className="api-outcome-label">Illustrative result</span>
              <dl className="api-outcome-result">
                {RUN_RESULT.map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd
                      className={
                        row.tone === "good"
                          ? "api-outcome-value--good"
                          : undefined
                      }
                    >
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="api-outcome-note">
                Webhooks fire on every transition, so your team or product knows the
                moment a worker needs a human or finishes the job.
              </p>
            </aside>
          </div>
        </section>

        <section className="api-cta">
          <h2>Start with the work.</h2>
          <p>
            Start with one workflow. Scale across departments, client accounts,
            products, or an entire business. One worker infrastructure, built
            around the outcome you need.
          </p>
          <div className="api-cta-actions">
            <a
              href={SCHEDULER_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="desktop-download-btn desktop-download-btn--primary"
            >
              Request Early Access
            </a>
            <Link
              to="/documentation"
              className="desktop-download-btn desktop-download-btn--secondary"
            >
              Read the Docs
            </Link>
          </div>
        </section>
      </main>

      <Footer theme="light" />
    </div>
  );
}
